<template>
  <div
    v-if="shouldDisplayWidget"
    class="offer-floa-widget"
    data-testid="offer-floa-widget">
    <div
      :id="widgetId"
      ref="widgetElement"
      class="offer-floa-widget__container"
      data-floa-offers></div>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, nextTick, ref, watch, type PropType } from 'vue';
import { storeToRefs } from 'pinia';
import { useProjectStore } from '@/stores/projectStore';
import offersHelper from '@/utils/helpers/offers';
import type { Offer } from '@/types/Offers';
import { loadFloaWidgetScript } from '@/utils/floaWidget';

const FLOA_4X_PRODUCT_CODE = 'BC4XC';
const FLOA_PNF_URL = 'https://www.floabank.fr/conditions-generales-paiement-plusieurs-fois';
const props = defineProps({
  hidePriceInfo: {
    type: Boolean,
    default: false,
  },
  offer: {
    type: Object as PropType<Offer>,
    required: true,
  },
});

const projectStore = useProjectStore();
const { projectSelected } = storeToRefs(projectStore);
const widgetElement = ref<HTMLElement | null>(null);
const instance = getCurrentInstance();

const sanitizeWidgetId = (value: string) => value.replace(/[^a-zA-Z0-9_-]/g, '-');

const widgetId = sanitizeWidgetId(`offer-floa-widget-${props.offer?.id || instance?.uid || 'quote'}`);

const amount = computed(() => {
  return offersHelper.getPrice(props.offer, false);
});

const amountInCents = computed(() => {
  return Math.round(amount.value * 100);
});

const shouldDisplayWidget = computed(() => {
  return Boolean(
    props.offer?.id
    && !props.hidePriceInfo
    && projectSelected.value?.params?.is_individual
    && (!!props.offer?.floa_payment || props.offer?.is_floa_allowed)
  );
});

const resetWidget = () => {
  window.cleanupFloaWidget?.();

  if (widgetElement.value) {
    widgetElement.value.innerHTML = '';
  }
};

const initWidget = async () => {
  if (!shouldDisplayWidget.value) {
    resetWidget();
    return;
  }

  try {
    await loadFloaWidgetScript();
    await nextTick();

    if (!shouldDisplayWidget.value || !widgetElement.value) {
      return;
    }

    window.initFloaWidget?.({
      amount: String(amountInCents.value),
      country: 'FR',
      custom_selector: `#${widgetId}`,
      locale: 'fr',
      offers: [FLOA_4X_PRODUCT_CODE],
      pnf: FLOA_PNF_URL,
      theme: 'brand',
    });
  } catch (error) {
    console.error(error);
    resetWidget();
  }
};

watch(
  () => [shouldDisplayWidget.value, amountInCents.value],
  async ([visible]) => {
    if (!visible) {
      resetWidget();
      return;
    }

    await initWidget();
  },
  {
    flush: 'post',
    immediate: true,
  },
);
</script>

<style scoped lang="scss">
.offer-floa-widget {
  display: flex !important;
  justify-content: center !important;
  width: 100% !important;
  margin-top: -20px !important;
  margin-bottom: 10px !important;

  &__container {
    all: revert;
    display: flex;
    justify-content: center;
    min-height: 40px;
    max-width: 100%;
  }

  :deep(.floa-widget.fl-widget) {
    display: flex !important;
    justify-content: center !important;
    margin-left: auto !important;
    margin-right: auto !important;
  }
}
</style>
